// src/utils/annualReportDates.js
// Mirrors server/annualReport.js's STATE_RULES due-date logic so the UI
// can compute a live countdown with zero network round-trips. This is
// intentionally duplicated, not fetched — keep dueDate() logic here in
// sync with the backend if you ever change it there.

export const STATE_RULES = {
  DE: {
    name: 'Delaware',
    label: 'Annual Franchise Tax',
    fee: '$300 flat (LLC/LP — no report content, payment only)',
    dueDate: () => '06-01',
    // Flat statutory date — every Delaware LLC owes it June 1st regardless
    // of when the LLC was formed. Formation date plays no role here.
    anniversaryBased: false,
    portalUrl: 'https://icis.corp.delaware.gov/ecorp/logintax.aspx?FilingType=FranchiseTax',
    verifyUrl: 'https://corp.delaware.gov/paytaxes/',
    needsFileNumber: true,
  },
  WY: {
    name: 'Wyoming',
    label: 'Annual Report + License Tax',
    fee: '$60 minimum (based on assets located in Wyoming)',
    dueDate: (formationDate) => {
      if (!formationDate) return null; // no guessing on the client — UI shows a prompt instead
      // Read the month straight out of the "YYYY-MM-DD" string rather than
      // going through `new Date(...)`. Parsing that string as UTC and then
      // reading .getMonth() back in the browser's local timezone can roll
      // the date to the previous day near a month boundary, silently
      // flipping the derived month — this sidesteps timezones entirely.
      const mm = formationDate.split('-')[1];
      return mm ? `${mm}-01` : null;
    },
    // Due on the 1st of the LLC's anniversary month — genuinely depends on
    // formation date, unlike Delaware above.
    anniversaryBased: true,
    portalUrl: 'https://wyobiz.wyo.gov/business/AnnualReport.aspx',
    verifyUrl: 'https://sos.wyo.gov/Business/AnnualReports.aspx',
    needsFileNumber: true,
  },
};

export function computeDueDate(stateCode, formationDate) {
  const rule = STATE_RULES[stateCode];
  if (!rule) return null;

  const mmdd = rule.dueDate(formationDate);
  if (!mmdd) return { rule, dueDateISO: null, daysRemaining: null, urgency: null };

  const now = new Date();
  const year = now.getFullYear();
  let dueDateISO = `${year}-${mmdd}`;
  let dueDate = new Date(dueDateISO + 'T00:00:00');
  if (dueDate < now) {
    dueDateISO = `${year + 1}-${mmdd}`;
    dueDate = new Date(dueDateISO + 'T00:00:00');
  }

  // For anniversary-based states, the FIRST report isn't due right after
  // formation — it's due roughly a year later, on the next anniversary of
  // the formation month. Without this check, an LLC formed this month (or
  // formed years ago in a month that's coming up soon) would show a due
  // date days away, as if the report were owed almost immediately — that's
  // never actually how these work. If the nearest calendar occurrence of
  // the due month falls less than ~11 months after formation, it isn't a
  // real anniversary yet, so push it out one more year.
  //
  // BUG FIX: formationDate is already a full "YYYY-MM-DD" string (from the
  // frontend's <input type="date">, same as everywhere else in this app).
  // This previously built `${formationDate}-01T00:00:00`, which appends a
  // redundant "-01" day segment onto a string that already has a day,
  // producing an invalid date string (e.g. "2026-01-15-01T00:00:00") that
  // `new Date(...)` silently parses as Invalid Date. Every comparison
  // against an Invalid Date evaluates to false, so this entire guard block
  // was a silent no-op — an LLC formed this month showed a due date days
  // away instead of ~11 months out. Parse the string directly; no "-01"
  // suffix needed since the day is already present.
  if (rule.anniversaryBased && formationDate) {
    const formedAt = new Date(`${formationDate}T00:00:00`);
    const earliestPlausibleDue = new Date(formedAt);
    earliestPlausibleDue.setMonth(earliestPlausibleDue.getMonth() + 11);
    if (dueDate < earliestPlausibleDue) {
      const pushedYear = parseInt(dueDateISO.slice(0, 4), 10) + 1;
      dueDateISO = `${pushedYear}-${mmdd}`;
      dueDate = new Date(dueDateISO + 'T00:00:00');
    }
  }

  const daysRemaining = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));

  let urgency = 'ok';
  if (daysRemaining <= 14) urgency = 'critical';
  else if (daysRemaining <= 60) urgency = 'soon';

  return { rule, dueDateISO, daysRemaining, urgency };
}